// The Stats rework's nightly files (Track 2, T2-12a) — one per competition-season, in S3. The
// browser downloads her league's file and Ben's chip code filters it (T2-12 "B split by league",
// docs/frontend-v3/track2-plan.md §9). The contract lives here so the job that writes a file and
// the site that reads it can't drift apart.
//
// Lean on purpose (fork 2): one header row of column names, then rows of plain values — ~4×
// smaller than named fields. Values are rounded to 4 significant digits (plenty for a bar).

/** What a row is about. */
export type StatsRowKind =
  /** All her minutes in this competition-season (one per player). */
  | "total"
  /** Only her minutes at one position group (the Position chip, the Goalkeeping exception). In
   *  a StatsAllFile (v2): her whole season at that group, every competition added up. */
  | "group"
  /** Every competition she played that season, added up ("All competitions"; domestic
   *  competitions only). In its own file (StatsAllFile), not in StatsFile. */
  | "all";

/** The fixed columns every row starts with; the engine's metric keys follow, in `metricKeys` order. */
export const STATS_FILE_FIXED_COLUMNS = ["player_id", "kind", "position_group", "team_id", "minutes"] as const;

/** One row: the fixed columns, then one value per metric (null = the engine had none). */
export type StatsFileRow = [
  playerId: number,
  kind: StatsRowKind,
  positionGroup: string | null,
  teamId: number | null,
  minutes: number,
  ...metrics: (number | null)[],
];

/** Who a player is — the chip fields that don't change row to row. */
export interface StatsFilePlayer {
  name: string;
  /** ISO "YYYY-MM-DD" (Age chip); null for ~24% of women — they drop out once an Age chip is on. */
  dob: string | null;
  /** Nationality chip. */
  country: string | null;
}

export interface StatsFile {
  version: 1;
  competitionId: number;
  seasonId: number;
  /** The competition's gender flag (`competition_womens`) — men are only compared with men. */
  womens: boolean;
  /** ISO time the nightly job built it. */
  builtAt: string;
  /** The metric columns after the fixed ones (every engine metric, fork 1). */
  metricKeys: string[];
  /** "total" + "group" rows. Her "all" row is in the StatsAllFile of the same competition-season.
   *  `team_id` = the club of her latest match here (Q-007: a same-league mover is one dot, with
   *  the club she finished with; until 2b's rebuild an older file has her most-minutes club). */
  rows: StatsFileRow[];
  /** Keyed by player id (as a string — JSON object keys). */
  players: Record<string, StatsFilePlayer>;
  /** Club names, keyed by team id. */
  teams: Record<string, string>;
}

/** "All competitions" for one domestic competition-season: her whole season added up across
 *  every competition, for each player in its StatsFile (same columns, same metricKeys). Its own
 *  file because it's made later — at 04:00, once every competition of the season is built — by
 *  adding up the builds' raw counts (no extra DB query).
 *
 *  Two formats live side by side until the full rebuild (T2-12b 2b): read `version`, never guess
 *  — a v1 file simply doesn't offer what it lacks. */
export type StatsAllFile = StatsAllFileV1 | StatsAllFileV2;

interface StatsAllFileBase {
  competitionId: number;
  seasonId: number;
  womens: boolean;
  builtAt: string;
  metricKeys: string[];
  /** How many competition-seasons were added up (that season, that gender). */
  competitions: number;
}

/** Before 2b: "all" rows only; players + clubs are in the StatsFile. */
export interface StatsAllFileV1 extends StatsAllFileBase {
  version: 1;
  rows: StatsFileRow[];
}

/** From 2b: "all" rows + "group" rows (her season at each position group — the Position chip and
 *  Goalkeeping on "All competitions"), and the chip fields, so one download is enough. Her club
 *  (`team_id`) = her club in THIS competition. */
export interface StatsAllFileV2 extends StatsAllFileBase {
  version: 2;
  rows: StatsFileRow[];
  players: Record<string, StatsFilePlayer>;
  teams: Record<string, string>;
}

/** A player who changed league mid-season: Ben's rule (Q-007, `docs/modules/rules.md`) makes her
 *  one dot, in the league she finished the season in — so she's left out of the group of each
 *  league she left (cups keep her). Domestic competitions only. */
export interface StatsMover {
  playerId: number;
  /** The league she finished the season in (her latest match's club → its league). */
  finished: number;
  /** The leagues she left (every game there before her new club's first). */
  left: number[];
}

/** One season's movers, one gender (made at 04:00 with "All competitions"). A player at two
 *  teams at the same time (youth / reserves + first team) isn't a mover. */
export interface StatsMoversFile {
  version: 1;
  seasonId: number;
  womens: boolean;
  builtAt: string;
  movers: StatsMover[];
}

/** The whole-database track for one gender: lowest + highest value per metric, over every
 *  player's "total" row in every competition-season (Ben's rules: the track doesn't move with
 *  the chips, and it's the same gender as her). */
export interface StatsTrackFile {
  version: 1;
  womens: boolean;
  builtAt: string;
  /** How many competition-seasons it was combined from (grows during the backfill). */
  competitionSeasons: number;
  ranges: Record<string, [min: number, max: number]>;
}

/** `GET /stats-file-seasons?competitionId=` — which seasons of one competition have a Stats file
 *  (the Stats tile's Season chip: its options, and "every season" once it's removed). Newest
 *  first. */
/** GET /stats-file-leagues: every competition with at least one Stats file, and its built seasons
 *  (Ben, 2026-10-18: the League chip lists only leagues with data). */
export interface StatsFileLeagues {
  leagues: { competitionId: number; seasonIds: number[] }[];
}

export interface StatsFileSeasons {
  competitionId: number;
  seasons: {
    seasonId: number;
    builtAt: string;
    /** Its "All competitions" file exists too (domestic competitions, once the 04:00 step ran). */
    all: boolean;
  }[];
}

/** `GET /stats-file` — a short-lived link to download one file straight from S3 (a Lambda
 *  can't send more than 6 MB, and a file is often bigger once unzipped). */
export interface StatsFileLink {
  url: string;
  builtAt: string;
  /** Seconds the link stays valid. */
  expiresIn: number;
}

/** Round to 4 significant digits — what a bar or a "0.53" needs, and much smaller in the file. */
export function roundSig(value: number): number {
  return Number(value.toPrecision(4));
}
